import { createContext } from "preact";
import { useCallback, useContext, useEffect, useReducer } from "preact/hooks";
import { retrieveImagesData, scrapThePage, resetMwlImgClass, zoomAnimation, retrieveVideosData } from "./helpers";
import { selector, anti_selector, disable_scroll, deep_linking, deep_linking_slug, separate_galleries, animation_toggle } from "./settings";

const controller_anti_selector = ".leaflet-tile, .leaflet-google-mutant";

const imagesHaveChanged = (images, newImages) => {
  if (newImages.length !== images.length) {
    return true;
  }

  const currentIds = images.map(v => v.id);
  return newImages.some(a => !currentIds.includes(a.id));
};

/**
 * Get correct deep-linking URL for an image ID
 * @param {string} index [image id]
 * @return {string} URL
 */
const mwl_pro_create_deeplinking_hash = (index) => {
  if (!isNaN(parseFloat(index)) && isFinite(index)) {
    window.location.hash = deep_linking_slug + '-' + index;
    return window.location.href;
  }
};

/**
 * Removes deeplinking hash
 * @return void
 */
const mwl_pro_remove_deeplinking_hash = () => {
  history.pushState("", document.title, window.location.pathname + window.location.search);
};

/****************************************
  Initial state
****************************************/
const initialState = {
  image: undefined,
  images: [],
  imagesNumber: 0,
  currentIndex: undefined,
  currentSeparatedIndexDifference: 0,
  autoplayInterval: undefined,
  currentUrl: false,
  mwlElement: undefined,
  isExtended: false,
  isPlaying: false,
  isMapOpen: false,
  postLoaded: false,
  animatedSlide: false,
  anti_selector: anti_selector
    ? [anti_selector, controller_anti_selector].join(", ")
    : controller_anti_selector,
  initialized: false,
};

/****************************************
  Action types
****************************************/

const SET_IMAGES = "SET_IMAGES";
const SET_IMAGES_NUMBER = "SET_IMAGES_NUMBER";
const CURRENT_INDEX_UPDATED = "CURRENT_INDEX_UPDATED";
const CURRENT_SEPARATED_INDEX_DIFFERENCE_UPDATED = "CURRENT_SEPARATED_INDEX_DIFFERENCE_UPDATED";
const AUTOPLAY_INTERVAL_UPDATED = "AUTOPLAY_INTERVAL_UPDATED";
const CURRENT_URL_UPDATED = "CURRENT_URL_UPDATED";
const SET_MWL_ELEMENT = "SET_MWL_ELEMENT";
const IS_EXTENDED_UPDATED = "IS_EXTENDED_UPDATED";
const IS_PLAYING_UPDATED = "IS_PLAYING_UPDATED";
const IS_MAP_OPEN_UPDATED = "IS_MAP_OPEN_UPDATED";
const POST_LOADED_UPDATED = "POST_LOADED_UPDATED";
const SET_INITIALIZED = "SET_INITIALIZED";

/****************************************
  Global reducer
****************************************/

const globalStateReducer = (state, action) => {
  switch (action.type) {
  case SET_IMAGES: {
    const { images } = action;
    return { ...state, images };
  }

  case SET_IMAGES_NUMBER: {
    const { imagesNumber } = action;
    return { ...state, imagesNumber };
  }

  case SET_MWL_ELEMENT: {
    const { mwlElement } = action;
    return { ...state, mwlElement };
  }

  case CURRENT_SEPARATED_INDEX_DIFFERENCE_UPDATED: {
    const { currentSeparatedIndexDifference } = action;
    return { ...state, currentSeparatedIndexDifference };
  }

  case CURRENT_INDEX_UPDATED: {
    const { currentIndex, animatedSlide = false } = action;
    const image = isNaN(currentIndex) ? undefined : state.images[currentIndex];

    let currentUrl = state.currentUrl;
    if (state.images.length > 0 && deep_linking && typeof mwl_pro_create_deeplinking_hash === "function") {
      if (currentIndex === false) {
        mwl_pro_remove_deeplinking_hash();
      } else {
        currentUrl = mwl_pro_create_deeplinking_hash(state.images[currentIndex].id);
      }
    }
    return { ...state, currentIndex, image, currentUrl, animatedSlide };
  }

  case AUTOPLAY_INTERVAL_UPDATED: {
    const { autoplayInterval } = action;
    return { ...state, autoplayInterval };
  }

  case CURRENT_URL_UPDATED: {
    const { currentUrl } = action;
    return { ...state, currentUrl };
  }

  case IS_EXTENDED_UPDATED: {
    const { isExtended } = action;
    return { ...state, isExtended };
  }

  case IS_PLAYING_UPDATED: {
    const { isPlaying } = action;
    return { ...state, isPlaying };
  }

  case IS_MAP_OPEN_UPDATED: {
    const { isMapOpen } = action;
    return { ...state, isMapOpen };
  }

  case POST_LOADED_UPDATED: {
    const { postLoaded } = action;
    return { ...state, postLoaded };
  }

  case SET_INITIALIZED: {
    const { initialized } = action;
    return { ...state, initialized };
  }

  default:
    return state;
  }
};

/****************************************
  Global state
****************************************/

const MeowLightboxContext = createContext();

const useMeowLightboxContext = () => {
  const actions = {};
  const [state, dispatch] = useContext(MeowLightboxContext);

  // Update Values
  actions.updateCurrentIndex = (currentIndex) => {
    dispatch({ type: CURRENT_INDEX_UPDATED, currentIndex, animatedSlide: true });
  };
  actions.updateAutoplayInterval = (autoplayInterval) => {
    dispatch({ type: AUTOPLAY_INTERVAL_UPDATED, autoplayInterval });
  };
  actions.updateCurrentUrl = (index) => {
    if (state.images.length > 0 && deep_linking && typeof mwl_pro_create_deeplinking_hash === "function") {
      if (index === false) {
        mwl_pro_remove_deeplinking_hash();
      }
      else {
        const currentUrl = mwl_pro_create_deeplinking_hash(state.images[index].id);
        dispatch({ type: CURRENT_URL_UPDATED, currentUrl });
      }
    }
  };
  actions.updateIsExtended = (isExtended) => {
    dispatch({ type: IS_EXTENDED_UPDATED, isExtended });
  };
  actions.updateIsPlaying = (isPlaying) => {
    dispatch({ type: IS_PLAYING_UPDATED, isPlaying });
  };
  actions.updateIsMapOpen = (isMapOpen) => {
    dispatch({ type: IS_MAP_OPEN_UPDATED, isMapOpen });
  };

  return { ...state, ...actions };
};

/****************************************
  Global state provider
****************************************/

export const MeowLightboxContextProvider = ({ children, infiniteScrollableGalleries }) => {
  const [state, dispatch] = useReducer(globalStateReducer, { ...initialState });

  const { currentIndex, images, mwlElement, postLoaded, initialized } = state;

  const separatedGalleries = separate_galleries ?? false;
  const hasZoomAnimation = animation_toggle === 'zoom_in_out';

  const openLightbox = useCallback((index = 0) => {

    const $mwl = document.querySelector('.mwl');
    const isVisible = $mwl.classList.contains('visible');

    if (!isVisible) {
      $mwl.classList.add('visible');
    }
    $mwl.setAttribute('data-index', index);

    if ( hasZoomAnimation && !isVisible ) {
      const _img = document.querySelector(`.mwl-img[mwl-index="${index}"]`);
      let _img_dest = document.querySelector(`.mwl__slider__image[data-index="${index}"] > .mwl__slider__image__thumbnail > img.landscape`);
      _img_dest = _img_dest == undefined ? document.querySelector(`.mwl__slider__image[data-index="${index}"] > .mwl__slider__image__thumbnail > img`) : _img_dest;

      // If the image is not loaded yet, wait for it
      requestAnimationFrame(() => {
        if (_img_dest.complete) {
          zoomAnimation({ isOpening: true, _img, _img_dest, lightbox: $mwl });
        } else {
          _img_dest.onload = () => {
          zoomAnimation({ isOpening: true, _img, _img_dest, lightbox: $mwl });
          };
        }
      });
    }

    const body = document.querySelector('body');
    if (disable_scroll && !body.classList.contains('no-scroll')) {
      body.classList.add('no-scroll');
      $mwl.addEventListener('wheel', (e) => {
        // Check if the event target is inside the caption-wrapper element.
        if (e.target.closest('.caption-wrapper')) {
          // Let the event propagate and scroll the caption normally.
          return;
        }
        // Otherwise, prevent scrolling.
        e.preventDefault();
        e.stopPropagation();
      }, { passive: false });
    }
  }, []);

  const registerMwlImgListener = useCallback(() => {
    const images = document.querySelectorAll('.mwl-img');
    images.forEach($image => {
      
      // make the image focusable ( Keyboard navigation )
      $image.setAttribute('tabindex', '0');
      // support Enter/Space to open lightbox
      $image.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          $image.click();
        }
      });

      $image.onclick = (e) => {
        if (!$image.classList.contains('mwl-img-disabled')) {
          e.preventDefault();

          // Find the Parent Gallery (only mgl-gallery for now)
          const parent = $image.closest(selector);

          if( separatedGalleries && parent != null ){
            const parent_mgl_items = parent.querySelectorAll('.mwl-img');

            // Set the images number to the Parent Gallery's children
            dispatch({ type: SET_IMAGES_NUMBER, imagesNumber: parent_mgl_items.length });

            // Set the index of the image relative to the Parent Gallery
            const currentIndex = parseInt($image.getAttribute('mwl-index'));
            const currentSeparatedIndexDifference =  currentIndex - parseInt($image.getAttribute('mwl-index-relative'));

            dispatch({ type: CURRENT_SEPARATED_INDEX_DIFFERENCE_UPDATED, currentSeparatedIndexDifference });
            dispatch({ type: CURRENT_INDEX_UPDATED, currentIndex, animatedSlide: false });

          }else{

            const currentIndex = parseInt($image.getAttribute('mwl-index'));

            // Reset in case a non Meow Gallery is selected afterward
            if( separatedGalleries ){
              dispatch({ type: SET_IMAGES_NUMBER, imagesNumber: images.length });
              dispatch({ type: CURRENT_SEPARATED_INDEX_DIFFERENCE_UPDATED, currentSeparatedIndexDifference: 0 });
            }

            dispatch({ type: CURRENT_INDEX_UPDATED, currentIndex, animatedSlide: false });

          }

        }
      };
    });
    if (!mwlElement) {
      dispatch({ type: SET_MWL_ELEMENT, mwlElement: document.querySelector('.mwl') });
    }
    if (document.querySelector('.mwl').classList.contains('visible')) {
      openLightbox(currentIndex);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      const imagesNumber = await scrapThePage();
      const images = [ ...retrieveImagesData(), ...retrieveVideosData() ];
      dispatch({ type: SET_IMAGES_NUMBER, imagesNumber});
      dispatch({ type: SET_IMAGES, images });

      const ua = navigator.userAgent.toLowerCase();
      if (ua.indexOf('safari') != -1) {
        if (ua.indexOf('chrome') > -1) {
          // Chrome
        } else {
          // Safari
          document.querySelector('.mwl').classList.add('safari-browser');
        }
      }

      if (window.location.href.indexOf('#' + deep_linking_slug + '-') > 0) {
        
        const slugRegex = new RegExp(`(#${deep_linking_slug}-)([0-9])+`, 'gm');

        const rawKey = window.location.href.match(slugRegex);
        const key = parseInt(rawKey[0].match(/([0-9])+/gm)[0]);
        const newImages = retrieveImagesData();
        const currentIndex = newImages.findIndex(v => v.id === key);
        if (currentIndex !== -1) {
          dispatch({ type: CURRENT_INDEX_UPDATED, currentIndex, animatedSlide: false });
        }
      }
      registerMwlImgListener();

      dispatch({ type: SET_INITIALIZED, initialized: true });
    };
    init();
  }, []);

  useEffect(() => {
    /**
     * Listen to post-load event, meaning content has been added through AJAX
     */
    const postLoadListener = () => dispatch({ type: POST_LOADED_UPDATED, postLoaded: true });
    document.body.addEventListener('post-load', postLoadListener, false);
    return () => document.body.removeEventListener('post-load', postLoadListener, false);
  }, []);

  // Listen to infinite scroll
  useEffect(() => {
    if (infiniteScrollableGalleries.length > 0) {
      const config = { childList: true };
      const callback = () => {
        dispatch({ type: SET_IMAGES_NUMBER, imagesNumber: resetMwlImgClass() });
        dispatch({ type: SET_IMAGES, images: retrieveImagesData() });
        registerMwlImgListener();
      };
      const mutationObserver = new MutationObserver(callback);
      for (const node of infiniteScrollableGalleries) {
        mutationObserver.observe(node, config);
      }

      return () => mutationObserver.disconnect();
    }
  }, []);

  useEffect(() => {
    if (Number.isInteger(currentIndex)) {
      openLightbox(currentIndex);
    }
  }, [currentIndex, openLightbox]);

  useEffect(() => {
    if (!initialized || (!postLoaded && images.length)) {
      return;
    }

    const newImages = retrieveImagesData();
    if (imagesHaveChanged(images, newImages)) {
      registerMwlImgListener();
      dispatch({ type: SET_IMAGES_NUMBER, imagesNumber: scrapThePage() });
      dispatch({ type: SET_IMAGES, images: newImages });
    }

    if (postLoaded) {
      dispatch({ type: POST_LOADED_UPDATED, postLoaded: false });
    }
  }, [images, postLoaded, initialized]);

  return (
    <MeowLightboxContext.Provider value={[state, dispatch]}>
      {children}
    </MeowLightboxContext.Provider>
  );
};

export default useMeowLightboxContext;
